import type {
  FlatGraphEdge,
  FlatGraphNode,
} from '@/sections/insights/queryBuilder/types/FlatGraph';
import type { QueryAction } from '@/sections/insights/queryBuilder/types/QueryActions';
import type { QueryState } from '@/sections/insights/queryBuilder/types/QueryState';
import { flatten } from '@/sections/insights/queryBuilder/utils/flatGraphConversion';

/**
 * Reducer for QueryState management
 *
 * Handles all graph mutations immutably.
 * Based on the reducer from QueryEditorContext but operating on QueryState.
 */
export function queryStateReducer(
  state: QueryState | null,
  action: QueryAction
): QueryState | null {
  if (!state && action.type !== 'SET_QUERY') {
    console.warn('Cannot perform action without initialized query state');
    return state;
  }

  switch (action.type) {
    case 'SET_QUERY': {
      return {
        flatGraph: flatten(action.query.query),
        metadata: {
          id: action.query.id,
          name: action.query.name,
          description: action.query.description,
          folderId: action.query.folderId,
          type: action.query.type,
          limit: action.query.query.limit,
          orderBy: action.query.query.orderBy,
          descending: action.query.query.descending,
          shortest: action.query.query.shortest,
        },
        isDirty: false,
      };
    }

    case 'SET_DIRTY': {
      if (!state) return state;

      return {
        ...state,
        isDirty: action.isDirty,
      };
    }

    case 'UPDATE_METADATA': {
      if (!state) return state;

      return {
        ...state,
        metadata: {
          ...state.metadata,
          ...action.metadata,
        },
        isDirty: action.skipDirty ? state.isDirty : true,
      };
    }

    case 'UPDATE_NODE': {
      if (!state) return state;

      const updatedNodes = new Map(state.flatGraph.nodes);
      const existingNode = updatedNodes.get(action.nodeId);

      if (!existingNode) {
        console.error(`Node ${action.nodeId} not found`);
        return state;
      }

      updatedNodes.set(action.nodeId, {
        ...existingNode,
        data: action.data,
      });

      return {
        ...state,
        flatGraph: { ...state.flatGraph, nodes: updatedNodes },
        isDirty: true,
      };
    }

    case 'UPDATE_NODE_POSITION': {
      if (!state) return state;

      const updatedNodes = new Map(state.flatGraph.nodes);
      const existingNode = updatedNodes.get(action.nodeId);

      if (!existingNode) {
        console.error(`Node ${action.nodeId} not found`);
        return state;
      }

      updatedNodes.set(action.nodeId, {
        ...existingNode,
        uiState: {
          ...existingNode.uiState,
          position: action.position,
        },
      });

      return {
        ...state,
        flatGraph: { ...state.flatGraph, nodes: updatedNodes },
        isDirty: true,
      };
    }

    case 'UPDATE_EDGE': {
      if (!state) return state;

      const updatedEdges = new Map(state.flatGraph.edges);
      const existingEdge = updatedEdges.get(action.edgeId);

      if (!existingEdge) {
        console.error(`Edge ${action.edgeId} not found`);
        return state;
      }

      updatedEdges.set(action.edgeId, {
        ...existingEdge,
        data: action.data,
      });

      return {
        ...state,
        flatGraph: { ...state.flatGraph, edges: updatedEdges },
        isDirty: true,
      };
    }

    case 'ADD_NODE': {
      if (!state) return state;

      const updatedNodes = new Map(state.flatGraph.nodes);
      updatedNodes.set(action.node.id, action.node);

      return {
        ...state,
        flatGraph: { ...state.flatGraph, nodes: updatedNodes },
        isDirty: true,
      };
    }

    case 'ADD_EDGE': {
      if (!state) return state;

      const updatedEdges = new Map(state.flatGraph.edges);
      updatedEdges.set(action.edge.id, action.edge);

      return {
        ...state,
        flatGraph: { ...state.flatGraph, edges: updatedEdges },
        isDirty: true,
      };
    }

    case 'ADD_NODE_AFTER': {
      if (!state) return state;

      // Generate UUIDs for new node and edge
      const newNodeId = crypto.randomUUID();
      const newEdgeId = crypto.randomUUID();

      // Find the edge we're inserting after
      const afterEdge = state.flatGraph.edges.get(action.afterEdgeId);
      if (!afterEdge) {
        console.error(`Edge ${action.afterEdgeId} not found`);
        return state;
      }

      // Create new node with defaults
      const newNode: FlatGraphNode = {
        id: newNodeId,
        data: {
          labels: action.node?.data?.labels || [],
          filters: action.node?.data?.filters || [],
          ...action.node?.data,
        },
        uiState: action.node?.uiState,
      };

      // Create new edge connecting afterEdge's target to new node
      const newEdge: FlatGraphEdge = {
        id: newEdgeId,
        data: {
          label: [],
          optional: false,
        },
        sourceId: newNodeId,
        targetId: afterEdge.targetId,
        direction: 'target',
      };

      // Update maps
      const updatedNodes = new Map(state.flatGraph.nodes);
      updatedNodes.set(newNodeId, newNode);

      const updatedEdges = new Map(state.flatGraph.edges);
      // Update afterEdge to point to new node
      updatedEdges.set(action.afterEdgeId, {
        ...afterEdge,
        targetId: newNodeId,
      });
      // Add new edge
      updatedEdges.set(newEdgeId, newEdge);

      return {
        ...state,
        flatGraph: {
          ...state.flatGraph,
          nodes: updatedNodes,
          edges: updatedEdges,
        },
        isDirty: true,
      };
    }

    case 'ADD_EDGE_AFTER': {
      if (!state) return state;

      // Generate UUIDs
      const newEdgeId = crypto.randomUUID();
      const newNodeId = crypto.randomUUID();

      // Find the node we're inserting after
      const afterNode = state.flatGraph.nodes.get(action.afterNodeId);
      if (!afterNode) {
        console.error(`Node ${action.afterNodeId} not found`);
        return state;
      }

      // Find existing outgoing edge from afterNode (if any)
      const existingOutgoingEdge = Array.from(
        state.flatGraph.edges.values()
      ).find(e => e.sourceId === action.afterNodeId);

      // Create new target node with defaults
      const newNode: FlatGraphNode = {
        id: newNodeId,
        data: {
          labels: [],
          filters: [],
        },
      };

      // Create new edge from afterNode to newNode
      const newEdge: FlatGraphEdge = {
        id: newEdgeId,
        data: {
          label: action.edge?.data?.label || [],
          optional: action.edge?.data?.optional ?? false,
          ...action.edge?.data,
        },
        sourceId: action.afterNodeId,
        targetId: newNodeId,
        direction: 'target',
      };

      // Update maps
      const updatedNodes = new Map(state.flatGraph.nodes);
      updatedNodes.set(newNodeId, newNode);

      const updatedEdges = new Map(state.flatGraph.edges);
      updatedEdges.set(newEdgeId, newEdge);

      // If afterNode had an existing outgoing edge, update it to start from newNode
      // This maintains linearity: afterNode -> newEdge -> newNode -> existingEdge -> originalTarget
      if (existingOutgoingEdge) {
        updatedEdges.set(existingOutgoingEdge.id, {
          ...existingOutgoingEdge,
          sourceId: newNodeId,
        });
      }

      return {
        ...state,
        flatGraph: {
          ...state.flatGraph,
          nodes: updatedNodes,
          edges: updatedEdges,
        },
        isDirty: true,
      };
    }

    case 'REMOVE_NODE': {
      if (!state) return state;

      const updatedNodes = new Map(state.flatGraph.nodes);
      updatedNodes.delete(action.nodeId);

      // Cascade: Remove edges connected to this node
      const updatedEdges = new Map(state.flatGraph.edges);
      for (const [edgeId, edge] of updatedEdges) {
        if (
          edge.sourceId === action.nodeId ||
          edge.targetId === action.nodeId
        ) {
          updatedEdges.delete(edgeId);
        }
      }

      // Recalculate rootId if we deleted the root node
      let rootId = state.flatGraph.rootId;
      if (action.nodeId === state.flatGraph.rootId) {
        const remainingNodeIds = Array.from(updatedNodes.keys());

        const targetNodeIds = new Set(
          Array.from(updatedEdges.values()).map(edge => edge.targetId)
        );

        // Find first node that's not a target of any edge
        // (i.e., a node with no incoming edges, which should be a root)
        rootId =
          remainingNodeIds.find(nodeId => !targetNodeIds.has(nodeId)) ||
          remainingNodeIds[0] ||
          '';
      }

      return {
        ...state,
        flatGraph: {
          nodes: updatedNodes,
          edges: updatedEdges,
          rootId: rootId,
        },
        isDirty: true,
      };
    }

    case 'REMOVE_EDGE': {
      if (!state) return state;

      const updatedEdges = new Map(state.flatGraph.edges);
      updatedEdges.delete(action.edgeId);

      return {
        ...state,
        flatGraph: { ...state.flatGraph, edges: updatedEdges },
        isDirty: true,
      };
    }

    case 'CLEAR_ALL_POSITIONS': {
      if (!state) return state;

      const updatedNodes = new Map(state.flatGraph.nodes);
      for (const [nodeId, node] of updatedNodes) {
        updatedNodes.set(nodeId, {
          ...node,
          uiState: {
            ...node.uiState,
            position: undefined,
          },
        });
      }

      return {
        ...state,
        flatGraph: { ...state.flatGraph, nodes: updatedNodes },
        isDirty: true,
      };
    }

    case 'RESET_GRAPH': {
      if (!state) return state;

      return {
        ...state,
        flatGraph: action.flatGraph,
      };
    }

    case 'CLEAR_QUERY': {
      return null;
    }

    default:
      return state;
  }
}
